import { Box, Drawer, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileItem } from 'api';
import { useUpdateFileMutation } from 'api/mutations/useUpdateFileMutation';
import { useFoldersQuery } from 'api/queries/useFoldersQuery';
import { alert } from 'components';
import { useWindowSize } from 'hooks';
import { theme } from 'styles';

import {
  CancelButton,
  Dialog,
  DuplicateButton,
  FooterActions,
} from '../FolderEditCategoryModal/styled';

interface MoveFileModalProps {
  open: boolean;
  onClose: () => void;
  item: FileItem;
  categoryId: string;
  currentFolderId?: string;
  tag?: string;
}

const selectStyle = {
  borderRadius: '8px',
  fontSize: '14px',
  height: '44px',
  background: '#fff',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #BDBDBD',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${theme.palette.info.main}`,
  },
};

export function MoveFileModal({
  open,
  onClose,
  item,
  categoryId,
  currentFolderId,
  tag,
}: MoveFileModalProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const { folders, isLoading } = useFoldersQuery({ categoryId });
  const { updateFileMutation, isLoading: isSaving } = useUpdateFileMutation();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const handleClose = () => {
    setSelectedFolderId('');
    onClose();
  };
  const handleMove = async () => {
    if (!selectedFolderId) return;

    await updateFileMutation({
      id: item.id,
      display_name: item.display_name,
      category_ids: [categoryId],
      folder_id: selectedFolderId === '/' ? null : selectedFolderId,
    });
    alert.success(t('toasts.file.success.move'));

    handleClose();
  };

  const formContent = (
    <Box sx={{ px: 2, pb: 'env(safe-area-inset-bottom)' }}>
      <Typography variant="h6" fontWeight={700} fontSize="16px" mb={3}>
        {t('resources.documentCategories.files.moveFileModal.title', {
          fileName: item.display_name,
        })}
      </Typography>

      <Typography variant="body2" fontSize="14px" fontWeight={500} color="text.secondary" mb={0.75}>
        {t('resources.documentCategories.files.moveFileModal.selectFolder')}
      </Typography>

      <Select
        fullWidth
        displayEmpty
        value={selectedFolderId}
        onChange={(e: SelectChangeEvent<string>) => setSelectedFolderId(e.target.value)}
        renderValue={(value) => {
          if (!value) {
            return (
              <Typography color="text.disabled" fontSize="14px">
                {t('resources.documentCategories.files.moveFileModal.placeholders.selectFolder')}
              </Typography>
            );
          }
          return folders.find((f) => f.id === value)?.name ?? value;
        }}
        sx={selectStyle}
      >
        {isLoading && (
          <MenuItem value="" disabled>
            {t('resources.documentCategories.files.moveFileModal.loadingFolders')}
          </MenuItem>
        )}
        {!isLoading && folders.length === 0 && (
          <MenuItem value="" disabled>
            {t('resources.documentCategories.files.moveFileModal.placeholders.emptyFolders')}
          </MenuItem>
        )}
        {currentFolderId && (
          <MenuItem key="/" value="/" sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography fontSize="14px">{tag}</Typography>
            <Typography fontSize="11px" color="text.disabled" lineHeight={1.2}>
              /
            </Typography>
          </MenuItem>
        )}
        {folders
          .filter((f) => f.id !== currentFolderId)
          .map((folder) => (
            <MenuItem
              key={folder.id}
              value={folder.id}
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <Typography fontSize="14px">{folder.name}</Typography>
              {folder.path && (
                <Typography fontSize="11px" color="text.disabled" lineHeight={1.2}>
                  {folder.path}
                </Typography>
              )}
            </MenuItem>
          ))}
      </Select>

      <FooterActions>
        <CancelButton variant="outlined" onClick={handleClose}>
          {t('resources.documentCategories.files.moveFileModal.buttons.cancel')}
        </CancelButton>
        <DuplicateButton
          variant="contained"
          onClick={handleMove}
          disabled={!selectedFolderId || isSaving}
        >
          {t('resources.documentCategories.files.moveFileModal.buttons.move')}
        </DuplicateButton>
      </FooterActions>
    </Box>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: 'env(safe-area-inset-bottom)',
          paddingBottom: '16px',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: '2px',
          mx: 'auto',
          my: '12px',
        }}
      />
      {formContent}
    </Drawer>
  ) : (
    <Dialog open={open} onClose={handleClose}>
      {formContent}
    </Dialog>
  );
}
